import { Alert, Button } from "@cheatsheetz/core-ui";
import { isRouteErrorResponse, useRouteError } from "react-router-dom";

const RELOAD_KEY = "cheetsheetz:chunk-reload";

/**
 * Detects whether an error is a failed dynamic `import()` — typically caused
 * by stale chunk hashes after a deployment.
 */
function isChunkLoadError(error: unknown): boolean {
  if (error instanceof TypeError && error.message.includes("dynamically imported module")) {
    return true;
  }
  // Webkit/Safari phrasing
  if (error instanceof TypeError && error.message.includes("Importing a module script failed")) {
    return true;
  }
  return false;
}

/**
 * Router-level error boundary that catches chunk load failures after
 * deployments and automatically reloads the page once to pick up the new
 * asset manifest. If the reload doesn't help, it shows a friendly error.
 */
export function RouteErrorBoundary() {
  const error = useRouteError();

  // Auto-reload once for stale chunk errors
  if (isChunkLoadError(error)) {
    const alreadyReloaded = sessionStorage.getItem(RELOAD_KEY) === window.location.pathname;
    if (!alreadyReloaded) {
      sessionStorage.setItem(RELOAD_KEY, window.location.pathname);
      window.location.reload();
      return null;
    }
    // Already tried reloading — clear flag and show error UI
    sessionStorage.removeItem(RELOAD_KEY);
  } else {
    // Clear any stale reload flag for non-chunk errors
    sessionStorage.removeItem(RELOAD_KEY);
  }

  // Route-level 404s etc.
  if (isRouteErrorResponse(error)) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 px-4 py-20">
        <h1 className="text-4xl font-bold">{error.status}</h1>
        <p className="text-muted-fg">{error.statusText || "Page not found"}</p>
        <Button intent="primary" onPress={() => (window.location.href = "/")}>
          Go Home
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 px-4 py-20">
      <h1 className="text-2xl font-bold">Something went wrong</h1>
      <Alert variant="error">
        {error instanceof Error
          ? "A new version of the app may have been deployed. Please refresh to continue."
          : "An unexpected error occurred."}
      </Alert>
      <Button intent="primary" onPress={() => window.location.reload()}>
        Refresh
      </Button>
    </div>
  );
}
